import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast } from "react-toastify";
import { AlertTriangle, ArrowRight, MapPin } from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { parseDate } from "../shared/Utils";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { Link } from "react-router-dom";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { compareModels, modelVersion } from "../utils/modelOrder";

interface Model {
  max_tokens: number;
  update_date: string;
  knowledge_cutoff?: string;
  alias?: string;
  deprecation_date?: string | null;
  replacement_model?: string | null;
  [key: string]: any;
}

interface ModelList {
  [modelName: string]: Model;
}

type Status = "atual" | "rapido" | "deprecating" | "legacy";

function classify(name: string, m: Model): Status {
  if (m.deprecation_date) return "deprecating";
  if (modelVersion(name) >= 4) return "atual";
  return "legacy";
}

// Modelos com inferência e processamento realizados 100% em território nacional
// (soberania/residência de dados). Identificados pelo sufixo "br-sp" no nome ou alias.
function isNationalTerritory(name: string, m: Model): boolean {
  return `${name} ${m.alias ?? ""}`.toLowerCase().includes("br-sp");
}

function StatusBadge({ status, deprecationDate }: { status: Status; deprecationDate?: string | null }) {
  if (status === "atual") {
    return <Badge variant="success">Atual</Badge>;
  }
  if (status === "rapido") {
    return <Badge variant="accent">Rápido</Badge>;
  }
  if (status === "deprecating" && deprecationDate) {
    return (
      <Badge variant="warning" className="whitespace-normal text-left">
        <AlertTriangle className="h-3 w-3" />
        Descontinuação em {parseDate(deprecationDate)}
      </Badge>
    );
  }
  return <Badge variant="secondary">Legacy</Badge>;
}

export default function Models() {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<ModelList>({});
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const get = async () => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const response = await fetch(`${API_BASE}/api/chat/models`, {
          method: "GET",
          headers: { Authorization: `Auth ${token}` },
        });
        if (!response.ok) {
          reportApiError(response, { feature: "models.list", method: "GET" });
          throw new Error("Failed");
        }
        const data = await response.json();
        setModels(data["models"] || {});
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "models.list" });
        toast.error("Erro ao recuperar modelos disponíveis.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    get();
  }, [getAccessTokenSilently]);

  const rows = useMemo(() => {
    const visible = Object.keys(models).filter(
      (n) => !n.toLowerCase().includes("competidor")
    );
    // Order: atual → rapido → deprecating → legacy; within a group, newest
    // version first (s5 above the 4.x family), then by name desc
    const ordered = visible.sort((a, b) => {
      const order: Record<Status, number> = { atual: 0, rapido: 1, deprecating: 2, legacy: 3 };
      const oa = order[classify(a, models[a])];
      const ob = order[classify(b, models[b])];
      if (oa !== ob) return oa - ob;
      return compareModels(a, b);
    });
    return ordered;
  }, [models]);

  return (
    <section className="models-essential space-y-6" aria-labelledby="models-catalog-title">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="models-catalog-title" className="text-lg font-semibold">Catálogo de modelos</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="block">{loading ? "Carregando modelos…" : `${rows.length} ${rows.length === 1 ? "modelo disponível" : "modelos disponíveis"}`}</span>
            <span className="block">Compare os modelos e seus dados na tabela.</span>
          </p>
        </div>
        <Link to="/precos" className="inline-flex items-center gap-1 text-sm font-medium hover:underline">
          Comparar preços <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border" role="region" aria-label="Comparação de modelos" tabIndex={0}>
        <table className="w-full min-w-[1000px] table-fixed text-sm" aria-label="Catálogo de modelos">
          <colgroup><col className="w-[28%]" /><col className="w-[23%]" /><col className="w-[17%]" /><col className="w-[14%]" /><col className="w-[18%]" /></colgroup>
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>{["Modelo", "Outros nomes na API", "Contexto máximo", "Lançamento", "Status"].map(label=><th key={label} scope="col" className="px-4 py-3 text-left font-medium">{label}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? Array.from({length:6}).map((_,i)=><tr key={i} className="h-[72px] border-t border-border/60">
              {Array.from({length:5}).map((_,j)=><td key={j} className="px-4 py-3"><Skeleton className="h-5 w-3/4" /></td>)}
            </tr>) : rows.map(name=>{
              const model=models[name];
              const status=classify(name,model);
              const national=isNationalTerritory(name,model);
              const aliases=model.alias?.split(",").map(alias=>alias.trim()).filter(Boolean) || [];
              return <tr key={name} className="h-[72px] border-t border-border/60 hover:bg-muted/30">
                <th scope="row" className="px-4 py-3 text-left font-medium">
                  <span className="block [overflow-wrap:anywhere]">{name}</span>
                  {national && <span className="mt-1 flex items-center gap-1 text-xs font-normal text-muted-foreground" title="Inferência e processamento realizados em território nacional."><MapPin className="h-3 w-3 shrink-0" />Território nacional</span>}
                  {model.replacement_model && <span className="mt-1 block text-xs font-normal text-muted-foreground">Substituto: {model.replacement_model}</span>}
                </th>
                <td className="px-4 py-3 text-xs text-muted-foreground">{aliases.length ? aliases.map(alias=><code key={alias} className="block leading-5 [overflow-wrap:anywhere]">{alias}</code>) : "—"}</td>
                <td className="px-4 py-3 tabular-nums">{model.max_tokens.toLocaleString("pt-BR")} <span className="text-xs text-muted-foreground">tokens</span></td>
                <td className="px-4 py-3 tabular-nums">{parseDate(model.update_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={status} deprecationDate={model.deprecation_date} /></td>
              </tr>;
            })}
            {!loading && rows.length===0 && <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground">Nenhum modelo disponível no momento.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
